import React from "react";
import PageHero from "../components/shared/PageHero";
import SectionHeading from "../components/shared/SectionHeading";
import SectionDivider from "../components/decor/SectionDivider";
import CTASection from "../components/shared/CTASection";
import Mandala from "../components/decor/Mandala";
import { IMG } from "../data/site";
import { Quote, ScrollText, BookOpen, Sparkles, Landmark, Music4, MapPin } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const achievements = [
  {
    icon: Landmark,
    title: { en: "Reform of temple worship", kn: "ದೇವಸ್ಥಾನ ಪೂಜೆಯ ಸುಧಾರಣೆ" },
    text: { en: "Instituted classical procedures for daily Krishna puja and enshrined Sri Bhuvaraha and Sri Hayagriva at Sode.", kn: "ದೈನಂದಿನ ಕೃಷ್ಣ ಪೂಜೆಗೆ ಶಾಸ್ತ್ರೀಯ ವಿಧಾನಗಳನ್ನು ಸ್ಥಾಪಿಸಿ, ಸೋದೆಯಲ್ಲಿ ಶ್ರೀ ಭೂವರಾಹ ಮತ್ತು ಶ್ರೀ ಹಯಗ್ರೀವ ಮೂರ್ತಿಗಳನ್ನು ಪ್ರತಿಷ್ಠಾಪಿಸಿದರು." },
  },
  {
    icon: Sparkles,
    title: { en: "The Paryaya tradition", kn: "ಪರ್ಯಾಯ ಪರಂಪರೆ" },
    text: { en: "Established the two-yearly rotational Paryaya of Krishna worship at Udupi - a system that endures unchanged today.", kn: "ಉಡುಪಿಯ ಶ್ರೀ ಕೃಷ್ಣ ಪೂಜೆಯ ಎರಡು ವರ್ಷದ ಆವರ್ತನ ಪರ್ಯಾಯ ವ್ಯವಸ್ಥೆಯನ್ನು ಸ್ಥಾಪಿಸಿದರು - ಇಂದಿಗೂ ಬದಲಾವಣೆ ಇಲ್ಲದೆ ನಡೆಯುತ್ತಿದೆ." },
  },
  {
    icon: ScrollText,
    title: { en: "Yuktimallika", kn: "ಯುಕ್ತಿಮಲ್ಲಿಕಾ" },
    text: { en: "His monumental philosophical treatise establishing Dvaita through unassailable reasoning.", kn: "ಅಜೇಯ ತರ್ಕದ ಮೂಲಕ ದ್ವೈತ ಸಿದ್ಧಾಂತವನ್ನು ಸ್ಥಾಪಿಸುವ ಅವರ ಮಹಾ ದಾರ್ಶನಿಕ ಗ್ರಂಥ." },
  },
  {
    icon: BookOpen,
    title: { en: "Rukminisha Vijaya", kn: "ರುಕ್ಮಿಣೀಶ ವಿಜಯ" },
    text: { en: "A mahakavya on the life of Sri Krishna - a peak of Sanskrit devotional poetry.", kn: "ಶ್ರೀ ಕೃಷ್ಣನ ಜೀವನದ ಮೇಲಿನ ಮಹಾಕಾವ್ಯ - ಸಂಸ್ಕೃತ ಭಕ್ತಿ ಕಾವ್ಯದ ಶಿಖರ." },
  },
  {
    icon: MapPin,
    title: { en: "Teerthaprabandha", kn: "ತೀರ್ಥಪ್ರಬಂಧ" },
    text: { en: "A poetic account of his pan-India pilgrimages, spanning from Kashi to Kanyakumari.", kn: "ಕಾಶಿಯಿಂದ ಕನ್ಯಾಕುಮಾರಿಯವರೆಗಿನ ಭಾರತದಾದ್ಯಂತದ ತೀರ್ಥಯಾತ್ರೆಗಳ ಕಾವ್ಯಮಯ ವಿವರಣೆ." },
  },
  {
    icon: Music4,
    title: { en: "Haridasa Movement", kn: "ಹರಿದಾಸ ಚಳವಳಿ" },
    text: { en: "Composed hundreds of Kannada devaranamas, foundational to the Haridasa tradition of devotional music.", kn: "ಹರಿದಾಸ ಸಂಗೀತ ಪರಂಪರೆಗೆ ಆಧಾರವಾದ ನೂರಾರು ಕನ್ನಡ ದೇವರನಾಮಗಳನ್ನು ರಚಿಸಿದರು." },
  },
];

const works = [
  { title: "Yuktimallika", type: { en: "Philosophy", kn: "ದರ್ಶನ" }, desc: { en: "The definitive Dvaita treatise in five gunchhas - Sat, Prameya, Guna, Anandada and Nikshepa.", kn: "ಸತ್, ಪ್ರಮೇಯ, ಗುಣ, ಆನಂದದ ಮತ್ತು ನಿಕ್ಷೇಪ - ಐದು ಗುಂಚ್ಛಗಳಲ್ಲಿ ದ್ವೈತ ಸಿದ್ಧಾಂತದ ಪ್ರಮಾಣಿಕ ಗ್ರಂಥ." } },
  { title: "Rukminisha Vijaya", type: { en: "Mahakavya", kn: "ಮಹಾಕಾವ್ಯ" }, desc: { en: "A nineteen-canto Sanskrit epic on Krishna's marriage with Rukmini - celebrated for poetic brilliance.", kn: "ಕೃಷ್ಣ-ರುಕ್ಮಿಣಿ ವಿವಾಹದ ಮೇಲಿನ ಹತ್ತೊಂಬತ್ತು ಸರ್ಗಗಳ ಸಂಸ್ಕೃತ ಮಹಾಕಾವ್ಯ - ಕಾವ್ಯ ಸೌಂದರ್ಯಕ್ಕೆ ಪ್ರಸಿದ್ಧ." } },
  { title: "Teerthaprabandha", type: { en: "Travelogue", kn: "ಯಾತ್ರಾ ಕಾವ್ಯ" }, desc: { en: "Poetic descriptions of holy places visited during his pilgrimages.", kn: "ತೀರ್ಥಯಾತ್ರೆಗಳಲ್ಲಿ ಭೇಟಿ ನೀಡಿದ ಪವಿತ್ರ ಸ್ಥಳಗಳ ಕಾವ್ಯಮಯ ವರ್ಣನೆ." } },
  { title: "Dashavatara Stotra", type: { en: "Hymn", kn: "ಸ್ತೋತ್ರ" }, desc: { en: "Ecstatic hymn in praise of the ten avatars of Vishnu.", kn: "ವಿಷ್ಣುವಿನ ದಶಾವತಾರಗಳ ಸ್ತುತಿಯ ಆನಂದಮಯ ಸ್ತೋತ್ರ." } },
  { title: "Bhugola Varnana", type: { en: "Cosmography", kn: "ಭೂಗೋಳ ಶಾಸ್ತ್ರ" }, desc: { en: "A remarkable account of geography and cosmology in classical terms.", kn: "ಶಾಸ್ತ್ರೀಯ ಪರಿಭಾಷೆಯಲ್ಲಿ ಭೂಗೋಳ ಹಾಗೂ ಬ್ರಹ್ಮಾಂಡ ವಿಜ್ಞಾನದ ಗಮನಾರ್ಹ ವಿವರಣೆ." } },
  { title: "Sarasa Bharati Vilasa", type: { en: "Poetry", kn: "ಕಾವ್ಯ" }, desc: { en: "Poetic composition celebrating Bharati Devi and Sanskrit.", kn: "ಭಾರತಿ ದೇವಿ ಹಾಗೂ ಸಂಸ್ಕೃತವನ್ನು ಸ್ತುತಿಸುವ ಕಾವ್ಯ ರಚನೆ." } },
  { title: "Nyayaratnavali", type: { en: "Logic", kn: "ನ್ಯಾಯ" }, desc: { en: "A concise digest on Nyaya principles as applied to Dvaita.", kn: "ದ್ವೈತಕ್ಕೆ ಅನ್ವಯಿಸಿದ ನ್ಯಾಯ ತತ್ವಗಳ ಸಂಕ್ಷಿಪ್ತ ಗ್ರಂಥ." } },
  { title: "Devaranamas", type: { en: "Kannada Devotional", kn: "ಕನ್ನಡ ಭಕ್ತಿಗೀತೆಗಳು" }, desc: { en: "Hundreds of devotional songs in Kannada - Vagvajra, Sarasija, and many more.", kn: "ವಾಗ್ವಜ್ರ, ಸರಸಿಜ ಮತ್ತಿತರ ಅಂಕಿತನಾಮಗಳಲ್ಲಿ ರಚಿತವಾದ ನೂರಾರು ಕನ್ನಡ ಭಕ್ತಿಗೀತೆಗಳು." } },
];

const pilgrimages = [
  { place: { en: "Badari", kn: "ಬದರಿ" }, note: { en: "Northern pilgrimage - sacred abode of Sri Vedavyasa.", kn: "ಉತ್ತರದ ಯಾತ್ರೆ - ಶ್ರೀ ವೇದವ್ಯಾಸರ ಪವಿತ್ರ ನಿವಾಸ." } },
  { place: { en: "Kashi", kn: "ಕಾಶಿ" }, note: { en: "Where he composed devotional works honouring Vishvanatha.", kn: "ವಿಶ್ವನಾಥನನ್ನು ಸ್ತುತಿಸುತ್ತಾ ಭಕ್ತಿ ರಚನೆಗಳನ್ನು ಮಾಡಿದ ಸ್ಥಳ." } },
  { place: { en: "Prayaga", kn: "ಪ್ರಯಾಗ" }, note: { en: "Bathed at the confluence of the Ganga and Yamuna.", kn: "ಗಂಗಾ-ಯಮುನಾ ಸಂಗಮದಲ್ಲಿ ಸ್ನಾನ." } },
  { place: { en: "Puri", kn: "ಪುರಿ" }, note: { en: "Received darshana of Sri Jagannatha.", kn: "ಶ್ರೀ ಜಗನ್ನಾಥನ ದರ್ಶನ ಪಡೆದರು." } },
  { place: { en: "Rameshwaram", kn: "ರಾಮೇಶ್ವರ" }, note: { en: "Southernmost pilgrimage - worshipping Sri Rama's linga.", kn: "ದಕ್ಷಿಣದ ಕೊನೆಯ ಯಾತ್ರೆ - ಶ್ರೀ ರಾಮ ಲಿಂಗದ ಪೂಜೆ." } },
  { place: { en: "Dwaraka", kn: "ದ್ವಾರಕಾ" }, note: { en: "Sacred city of Krishna in the West.", kn: "ಪಶ್ಚಿಮದ ಕೃಷ್ಣನ ಪವಿತ್ರ ನಗರಿ." } },
  { place: { en: "Pandharpur", kn: "ಪಂಢರಪುರ" }, note: { en: "Where he composed devaranamas of Sri Vittala.", kn: "ಶ್ರೀ ವಿಠ್ಠಲನ ದೇವರನಾಮಗಳನ್ನು ರಚಿಸಿದ ಸ್ಥಳ." } },
  { place: { en: "Ahobilam", kn: "ಅಹೋಬಿಲ" }, note: { en: "The nine Narasimha shrines in the Deccan.", kn: "ದಖ್ಖನ್‌ನ ಒಂಬತ್ತು ನೃಸಿಂಹ ಕ್ಷೇತ್ರಗಳು." } },
];

const COPY = {
  heroEye: { en: "Jagadguru • Peethadhipati • Poet", kn: "ಜಗದ್ಗುರು • ಪೀಠಾಧಿಪತಿ • ಕವಿ" },
  heroT: { en: "Sri Vadiraja Theertha", kn: "ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥ" },
  heroS: { en: "Saint • Philosopher • Poet • Reformer - the spiritual luminary who transformed Sode Matha into a beacon of Dvaita and devotion.", kn: "ಸಂತ • ದಾರ್ಶನಿಕ • ಕವಿ • ಸುಧಾರಕ - ಸೋದೆ ಮಠವನ್ನು ದ್ವೈತ ಹಾಗೂ ಭಕ್ತಿಯ ದೀಪಸ್ತಂಭವಾಗಿ ಪರಿವರ್ತಿಸಿದ ಆಧ್ಯಾತ್ಮಿಕ ಜ್ಯೋತಿ." },
  introH1: { en: "Life & Legacy", kn: "ಜೀವನ ಮತ್ತು ಪರಂಪರೆ" },
  introH2: { en: "A saint whose light still illumines the tradition", kn: "ಇಂದಿಗೂ ಪರಂಪರೆಯನ್ನು ಬೆಳಗುತ್ತಿರುವ ಸಂತ" },
  introP1: { en: "Sri Vadiraja Theertha (1480 - 1600) was among the most extraordinary figures in the Dvaita tradition. As Peethadhipati of Sode Matha for over a century, he combined the depth of a philosopher, the grace of a poet, the discipline of a reformer, and the compassion of a saint.", kn: "ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು (೧೪೮೦-೧೬೦೦) ದ್ವೈತ ಪರಂಪರೆಯ ಅತ್ಯಂತ ಅಸಾಮಾನ್ಯ ವ್ಯಕ್ತಿಗಳಲ್ಲಿ ಒಬ್ಬರು. ಒಂದು ಶತಮಾನಕ್ಕೂ ಹೆಚ್ಚು ಕಾಲ ಸೋದೆ ಮಠದ ಪೀಠಾಧಿಪತಿಗಳಾಗಿ, ದಾರ್ಶನಿಕನ ಗಾಂಭೀರ್ಯ, ಕವಿಯ ಸೌಂದರ್ಯ, ಸುಧಾರಕನ ಶಿಸ್ತು ಹಾಗೂ ಸಂತನ ಕರುಣೆ - ಇವೆಲ್ಲವನ್ನೂ ಸಮನ್ವಯಗೊಳಿಸಿದವರು." },
  introP2: { en: "He instituted the two-yearly Paryaya rotation of Krishna worship at Udupi - an arrangement that has been observed unbroken for five centuries. He enshrined the Sri Bhuvaraha idol received from divine sources at Sode, and daily worshipped Sri Hayagriva with the offering of Hayagriva Maddi.", kn: "ಉಡುಪಿಯ ಶ್ರೀ ಕೃಷ್ಣ ಪೂಜೆಯ ಎರಡು ವರ್ಷದ ಆವರ್ತನ ಪರ್ಯಾಯ ವ್ಯವಸ್ಥೆಯನ್ನು ಸ್ಥಾಪಿಸಿದರು - ಇದು ಐದು ಶತಮಾನಗಳಿಂದ ಅಚ್ಛೇದಿತವಾಗಿ ನಡೆಯುತ್ತಿದೆ. ದೈವಿಕ ಮೂಲದಿಂದ ದೊರೆತ ಶ್ರೀ ಭೂವರಾಹ ಮೂರ್ತಿಯನ್ನು ಸೋದೆಯಲ್ಲಿ ಪ್ರತಿಷ್ಠಾಪಿಸಿ, ಶ್ರೀ ಹಯಗ್ರೀವನಿಗೆ ಹಯಗ್ರೀವ ಮದ್ದಿಯನ್ನು ಸಮರ್ಪಿಸಿ ದೈನಂದಿನ ಪೂಜಿಸಿದರು." },
  introP3: { en: "He authored philosophical masterpieces like Yuktimallika, mahakavyas like Rukminisha Vijaya, devotional stotras, and hundreds of Kannada devaranamas that became foundational to the Haridasa movement.", kn: "ಯುಕ್ತಿಮಲ್ಲಿಕಾದಂತಹ ದಾರ್ಶನಿಕ ಮೇರುಕೃತಿಗಳು, ರುಕ್ಮಿಣೀಶ ವಿಜಯದಂತಹ ಮಹಾಕಾವ್ಯಗಳು, ಭಕ್ತಿ ಸ್ತೋತ್ರಗಳು ಮತ್ತು ಹರಿದಾಸ ಚಳವಳಿಗೆ ಆಧಾರವಾದ ನೂರಾರು ಕನ್ನಡ ದೇವರನಾಮಗಳನ್ನು ರಚಿಸಿದರು." },
  years: { en: "120 years of luminous life", kn: "೧೨೦ ವರ್ಷಗಳ ಬೆಳಗುತ್ತಿರುವ ಜೀವನ" },
  achH: { en: "Achievements", kn: "ಸಾಧನೆಗಳು" },
  achT: { en: "Seven pillars of his enduring legacy", kn: "ಶಾಶ್ವತ ಪರಂಪರೆಯ ಆಧಾರಸ್ತಂಭಗಳು" },
  worksH: { en: "Manuscript Gallery", kn: "ಗ್ರಂಥ ಗ್ಯಾಲರಿ" },
  worksT: { en: "Literary contributions", kn: "ಸಾಹಿತ್ಯ ಕೊಡುಗೆಗಳು" },
  worksS: { en: "From philosophical treatises to devaranamas - a lifetime of literary output.", kn: "ದಾರ್ಶನಿಕ ಗ್ರಂಥಗಳಿಂದ ದೇವರನಾಮಗಳವರೆಗೆ - ಒಂದು ಜೀವಮಾನದ ಸಾಹಿತ್ಯ ಸೃಷ್ಟಿ." },
  pilgH: { en: "Pan-India Pilgrimages", kn: "ಭಾರತವ್ಯಾಪಿ ತೀರ್ಥಯಾತ್ರೆಗಳು" },
  pilgT: { en: "Sacred journeys across Bharatavarsha", kn: "ಭಾರತವರ್ಷದಾದ್ಯಂತ ಪವಿತ್ರ ಯಾತ್ರೆಗಳು" },
  quote: { en: "By his devotion he touched the divine; by his philosophy he anchored a tradition; by his poetry he lit a thousand hearts.", kn: "ತನ್ನ ಭಕ್ತಿಯಿಂದ ದೈವಿಕವನ್ನು ಸ್ಪರ್ಶಿಸಿದನು; ತನ್ನ ದರ್ಶನದಿಂದ ಪರಂಪರೆಗೆ ಆಧಾರವಾದನು; ತನ್ನ ಕಾವ್ಯದಿಂದ ಸಹಸ್ರಾರು ಹೃದಯಗಳನ್ನು ಬೆಳಗಿಸಿದನು." },
  quoteAttr: { en: "Devotional Chronicle", kn: "ಭಕ್ತಿ ಇತಿಹಾಸ" },
  ctaEye: { en: "The unbroken lineage", kn: "ಅಚ್ಛೇದಿತ ಪರಂಪರೆ" },
  ctaT: { en: "Explore the Guru Parampara of Sode Matha", kn: "ಸೋದೆ ಮಠದ ಗುರುಪರಂಪರೆಯನ್ನು ಅನ್ವೇಷಿಸಿ" },
  ctaP: { en: "Guru Parampara", kn: "ಗುರುಪರಂಪರೆ" },
  ctaS: { en: "Deities & Worship", kn: "ದೇವರುಗಳು ಮತ್ತು ಪೂಜೆ" },
};

const Vadiraja = () => {
  const { lang } = useLang();
  const L = (o) => (o && o[lang]) || (o && o.en) || "";
  return (
    <>
      <PageHero eyebrow={L(COPY.heroEye)} title={L(COPY.heroT)} subtitle={L(COPY.heroS)} />

      <section className="py-16" data-testid="vadiraja-intro">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading eyebrow={L(COPY.introH1)} title={L(COPY.introH2)} />
            <div className="mt-8 space-y-4 text-[#3B2A1A]/85 leading-relaxed">
              <p>{L(COPY.introP1)}</p>
              <p>{L(COPY.introP2)}</p>
              <p>{L(COPY.introP3)}</p>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
              <img src={require("../assets/iwbp2dy8_Srivadiraja_Theertha_hires.jpeg")} alt="Sri Vadiraja Theertha" className="w-full h-full object-cover" data-testid="vadiraja-image" />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#B65A14] text-[#FBF7F0] p-5 rounded-2xl shadow-xl">
              <div className="overline text-[#D4AF37] mb-1">1480 - 1600</div>
              <p className="font-serif-display text-base leading-tight">{L(COPY.years)}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-sand-texture py-16 relative overflow-hidden" data-testid="vadiraja-achievements">
        <div className="absolute -bottom-24 -left-24 opacity-25 animate-mandala-slow"><Mandala size={340} color="#B65A14" opacity={0.5} /></div>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <SectionHeading eyebrow={L(COPY.achH)} title={L(COPY.achT)} align="center" />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {achievements.map((a, i) => (
              <div key={i} className="bg-[#FBF7F0] border border-[#D4AF37]/25 rounded-2xl p-8 card-lift" data-testid={`vadiraja-ach-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-[#B65A14] text-[#D4AF37] flex items-center justify-center mb-5">
                  <a.icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif-display text-xl text-[#3B2A1A] mb-2">{L(a.title)}</h3>
                <p className="text-sm text-[#3B2A1A]/80 leading-relaxed">{L(a.text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionDivider variant="mandala" />

      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-8" data-testid="vadiraja-works">
        <SectionHeading eyebrow={L(COPY.worksH)} title={L(COPY.worksT)} subtitle={L(COPY.worksS)} align="center" />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {works.map((w) => (
            <div
              key={w.title}
              className="relative bg-gradient-to-br from-[#F2E7D5] to-[#FBF7F0] border border-[#D4AF37]/40 rounded-2xl p-6 card-lift overflow-hidden"
              data-testid={`work-${w.title.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="absolute -top-6 -right-6 opacity-20"><Mandala size={90} color="#B65A14" opacity={1} /></div>
              <ScrollText className="w-8 h-8 text-[#B65A14] mb-4" />
              <div className="overline text-[#B65A14] mb-2">{L(w.type)}</div>
              <h3 className="font-serif-display text-lg text-[#3B2A1A] mb-2">{w.title}</h3>
              <p className="text-xs text-[#3B2A1A]/75 leading-relaxed">{L(w.desc)}</p>
            </div>
          ))}
        </div>
      </section>

      <SectionDivider variant="arch" />

      <section className="py-16 bg-sand-texture" data-testid="vadiraja-pilgrimages">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <SectionHeading eyebrow={L(COPY.pilgH)} title={L(COPY.pilgT)} align="center" />
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
            {pilgrimages.map((p, i) => (
              <div key={i} className="bg-[#FBF7F0] border border-[#D4AF37]/25 rounded-2xl p-6 text-center card-lift" data-testid={`pilg-${i}`}>
                <MapPin className="w-5 h-5 text-[#B65A14] mx-auto mb-3" />
                <div className="font-serif-display text-lg text-[#3B2A1A]">{L(p.place)}</div>
                <div className="text-xs text-[#3B2A1A]/70 mt-2 leading-relaxed">{L(p.note)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 lg:px-8 py-16 text-center" data-testid="vadiraja-legacy-quote">
        <Quote className="w-10 h-10 text-[#D4AF37] mx-auto mb-6" />
        <p className="font-serif-display text-2xl sm:text-3xl italic text-[#3B2A1A] leading-relaxed">
          {`"${L(COPY.quote)}"`}
        </p>
        <p className="mt-6 overline text-[#B65A14]">{L(COPY.quoteAttr)}</p>
      </section>

      <CTASection
        eyebrow={L(COPY.ctaEye)}
        title={L(COPY.ctaT)}
        primary={{ label: L(COPY.ctaP), to: "/lineage?tab=guru" }}
        secondary={{ label: L(COPY.ctaS), to: "/deities" }}
        image={IMG.processional}
      />
    </>
  );
};

export const VadirajaContent = Vadiraja;
export default Vadiraja;
